"use client";

import { FC, useRef } from "react";
import { useWindowSize } from "react-use";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";

import { ElProps } from "@Shared/types";
import { Text } from "@Shared/ui";
import { splitText, twcx } from "@Shared/utils";

export type LogoProps = ElProps<"div">;

export const Logo: FC<LogoProps> = ({ className, ...restProps }) => {
    const refComponent = useRef<HTMLDivElement>(null);
    const refText = useRef<HTMLDivElement>(null);

    const { width } = useWindowSize();

    useGSAP(
        () => {
            if (!refComponent.current || !refText.current) {
                return;
            }

            splitText({ element: refText.current });

            const $chars = refText.current.querySelectorAll(".char");

            if (!$chars.length) {
                return;
            }

            const $componentNextSibling = refComponent.current.nextElementSibling;

            gsap.set($chars, { willChange: "transform" });

            gsap.from($chars, {
                scrollTrigger: {
                    trigger: refComponent.current,
                    endTrigger: $componentNextSibling ?? undefined,
                    start: "top bottom",
                    end: "bottom bottom",
                    scrub: 3,
                },
                stagger: 0.15,
                yPercent: 101,
            });

            return () => {
                if ($chars?.length) {
                    gsap.set($chars, { clearProps: "all" });
                }
            };
        },
        {
            scope: refComponent,
            revertOnUpdate: true,
            dependencies: [width],
        }
    );

    return (
        <div
            ref={refComponent}
            className={twcx(
                "col-span-full -mb-10 flex justify-center overflow-hidden px-1 pt-[14px]",
                "lg:-mb-14 lg:px-0 lg:pt-8",
                className
            )}
            {...restProps}
        >
            <Text
                ref={refText}
                className={twcx(
                    "mb-[calc(-12_/_360_*_100vw)] text-[calc(86_/_360_*_100vw)] leading-[80%] tracking-[calc(-2.1_/_360_*_100vw)]",
                    "md:-mb-6 md:text-[180px] md:tracking-[-2px]",
                    "lg:-mb-8 lg:text-[240px]",
                    "xl:-mb-10 xl:-ml-3 xl:text-[320px]"
                )}
                family="brand"
                weight={800}
                align="center"
            >
                NeoLeaf
            </Text>
        </div>
    );
};
